import ArticleImage from "../components/ArticleImage";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import EditorialArticleHero from "../components/EditorialArticleHero";
import TechArticleAuthorBox from "../components/TechArticleAuthorBox";
import XPostEmbed from "../components/XPostEmbed";
import ArticleShareBlock from "../components/ArticleShareBlock";
import FollowTechniaHQ from "../components/FollowTechniaHQ";
import ArticleAd from "../ads/ArticleAd";
import SidebarAd from "../ads/SidebarAd";
import { seoConfig } from "../data/seoConfig";
import { xRoboticsArticles } from "../data/xRoboticsArticles";

function ArticleNotFound() {
  return (
    <section className="site-shell py-16">
      <div className="soft-card p-8">
        <p className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
          Robotics
        </p>
        <h1 className="mt-4 text-3xl font-black text-neutral-950">
          Robotics article not found
        </h1>
        <p className="mt-3 text-neutral-600">
          Open the News section to choose another TechniaHQRobot article.
        </p>
        <Link to="/robotics-news" className="btn-primary mt-6">
          Back to News
        </Link>
      </div>
    </section>
  );
}

function KeyDetails({ items }) {
  if (!items?.length) return null;
  return (
    <section
      className="mt-8 grid gap-3 sm:grid-cols-2"
      aria-label="Key details"
    >
      {items.slice(0, 4).map((item) => (
        <div
          key={item}
          className="rounded-[1.35rem] border border-neutral-200 bg-white p-5 shadow-[0_14px_45px_rgba(15,23,42,0.05)]"
        >
          <p className="text-sm font-semibold leading-7 text-neutral-700">
            {item}
          </p>
        </div>
      ))}
    </section>
  );
}

function TechCard({ data }) {
  const entries = Object.entries(data || {}).filter(
    ([, value]) => value && String(value).trim(),
  );
  if (!entries.length) return null;
  return (
    <section
      className="mt-8 rounded-[1.6rem] border border-neutral-200 bg-neutral-950 p-5 text-white sm:p-6"
      aria-label="Technical details"
    >
      <h2 className="text-sm font-black uppercase tracking-[0.18em] text-blue-200">
        Technical details
      </h2>
      <dl className="mt-5 grid gap-3">
        {entries.map(([label, value]) => (
          <div
            key={label}
            className="grid gap-1 rounded-[1rem] border border-white/10 bg-white/5 p-4 sm:grid-cols-[12rem_1fr] sm:gap-4"
          >
            <dt className="text-xs font-black uppercase tracking-[0.16em] text-blue-200">
              {label}
            </dt>
            <dd className="text-sm font-semibold leading-7 text-white/90">
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function VerificationNotes({ notes }) {
  if (!notes?.length) return null;
  return (
    <section
      className="mt-10 rounded-[1.6rem] border border-amber-200 bg-amber-50 p-5 sm:p-6"
      aria-labelledby="verification-notes-heading"
    >
      <h2
        id="verification-notes-heading"
        className="text-sm font-black uppercase tracking-[0.18em] text-amber-900"
      >
        Verification notes
      </h2>
      <ul className="mt-4 grid gap-3 text-sm leading-7 text-amber-950">
        {notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </section>
  );
}

function RelatedLinks({ links }) {
  if (!links?.length) return null;
  return (
    <section
      className="mt-10 rounded-[1.6rem] border border-neutral-200 bg-neutral-50 p-5 sm:p-6"
      aria-labelledby="related-articles-heading"
    >
      <h2
        id="related-articles-heading"
        className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500"
      >
        Related articles
      </h2>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {links.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="rounded-[1.15rem] border border-neutral-200 bg-white p-4 text-sm font-black leading-6 text-neutral-900 transition hover:border-blue-200 hover:text-blue-700"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

function TopicHubLinks({ article }) {
  const text = `${article.title} ${article.category || ""} ${(article.keywords || []).join(" ")}`.toLowerCase();
  const links = [];
  if (/humanoid|atlas|stardust/.test(text)) links.push(["Humanoid robots", "/humanoid-robots"]);
  if (/physical ai|embodied|foundation model|vla|lingbot|perceptron/.test(text)) links.push(["Physical AI foundation models", "/physical-ai"]);
  if (/teleoperation|dataset|data|annotation|perceptron/.test(text)) links.push(["Robotics data collection", "/robotics-data-collection"]);
  if (/hand|gripper|actuator|hardware/.test(text)) links.push(["Robot components and hardware", "/robot-components"]);
  if (/software|model|control/.test(text)) links.push(["Robotics software", "/robotics-software"]);
  if (/military|robot dog|quadruped/.test(text)) links.push(["Robot dogs and quadrupeds", "/robot-types/robot-dogs-quadrupeds"]);
  if (/tracked|ugv|unmanned ground/.test(text)) links.push(["Tracked robots and UGVs", "/robot-types/tracked-robots"]);
  if (/manufacturing|factory|production line|optimus/.test(text)) links.push(["Industrial robots", "/robot-types/industrial-robots"]);
  if (/italy|italian/.test(text)) links.push(["Robotics companies", "/robotics-companies"]);
  const unique = Array.from(new Map(links.map((item) => [item[1], item])).values()).slice(0, 4);
  if (!unique.length) unique.push(["Latest robotics news", "/robotics-news"]);
  return (
    <section className="mt-10 rounded-[1.6rem] border border-blue-100 bg-blue-50 p-5 sm:p-6" aria-labelledby="topic-hubs-heading">
      <h2 id="topic-hubs-heading" className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">Explore the technical topic</h2>
      <div className="mt-4 flex flex-wrap gap-3">
        {unique.map(([label, to]) => <Link key={to} to={to} className="rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-black text-neutral-900 transition hover:border-blue-400 hover:text-blue-700">{label}</Link>)}
      </div>
    </section>
  );
}


function ArticleFaq({ items }) {
  if (!items?.length) return null;
  return (
    <section
      className="mt-10 rounded-[1.6rem] border border-neutral-200 bg-neutral-50 p-5 sm:p-6"
      aria-labelledby="article-faq-heading"
    >
      <h2
        id="article-faq-heading"
        className="text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl"
      >
        Frequently asked questions
      </h2>
      <div className="mt-5 grid gap-3">
        {items.map((item) => (
          <details
            key={item.question}
            className="group rounded-[1.15rem] border border-neutral-200 bg-white p-4"
          >
            <summary className="cursor-pointer list-none pr-8 text-base font-black leading-7 text-neutral-950 marker:content-none">
              {item.question}
            </summary>
            <p className="mt-3 text-sm leading-7 text-neutral-700 sm:text-base">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}


function RobbyantAttribution({ article }) {
  const articleText = `${article.title || ""} ${article.subtitle || ""} ${(article.tags || []).join(" ")} ${(article.keywords || []).join(" ")}`.toLowerCase();
  if (!/lingbot|robbyant/.test(articleText)) return null;

  return (
    <section
      className="mt-10 rounded-[1.6rem] border border-blue-200 bg-blue-50 p-5 sm:p-6"
      aria-labelledby="robbyant-source-heading"
    >
      <h2
        id="robbyant-source-heading"
        className="text-sm font-black uppercase tracking-[0.18em] text-blue-700"
      >
        Original technical source
      </h2>
      <p className="mt-4 text-base font-bold text-neutral-900">
        Follow Robbyant on X:
      </p>
      <a
        href="https://x.com/robbyant_brain"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-flex break-all text-base font-black text-blue-700 underline decoration-2 underline-offset-4 hover:text-blue-900"
      >
        @robbyant_brain
      </a>
    </section>
  );
}

function Sources({ sources, sponsored = false }) {
  if (!sources?.length) return null;
  return (
    <section
      className="mt-10 rounded-[1.6rem] border border-neutral-200 bg-white p-5 sm:p-6"
      aria-labelledby="sources-heading"
    >
      <h2
        id="sources-heading"
        className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500"
      >
        Sources
      </h2>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {sources.map((source) => (
          <a
            key={`${source.label}-${source.url}`}
            href={source.url}
            target="_blank"
            rel={sponsored ? "sponsored noopener noreferrer" : "noopener noreferrer"}
            className="rounded-[1.15rem] border border-neutral-200 bg-neutral-50 p-4 text-sm font-bold leading-6 text-neutral-800 transition hover:border-blue-200 hover:text-blue-700"
          >
            {source.label}
          </a>
        ))}
      </div>
    </section>
  );
}

export default function RoboticsXArticlePage() {
  const { slug } = useParams();
  const article = xRoboticsArticles.find((item) => item.slug === slug);
  const relatedInSidebar = useMemo(
    () =>
      xRoboticsArticles
        .filter((item) => item.slug !== slug)
        .sort((a, b) =>
          String(b.datePublished).localeCompare(String(a.datePublished)),
        )
        .slice(0, 5),
    [slug],
  );

  if (!article) return <ArticleNotFound />;

  const publisherName = article.authorName || "TechniaHQRobot";
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "News", to: "/robotics-news" },
    { label: article.title, to: article.path },
  ];
  const imageUrl = article.image?.startsWith("http")
    ? article.image
    : `${seoConfig.baseUrl}${article.image || "/og-image-tech-ai-2025.png"}`;
  const faqSchema = article.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }
    : null;

  const topicTag =
    article.tags?.find(
      (tag) => String(tag).toLowerCase() !== String(article.category || "").toLowerCase(),
    ) || article.controlMethod;
  const schema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.seoDescription || article.description,
    image: [imageUrl],
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      "@type": "Organization",
      name: publisherName,
      url: `${seoConfig.baseUrl}/about-techniahq`,
    },
    publisher: { "@type": "Organization", name: publisherName },
    articleSection: article.category,
    keywords: article.keywords?.join(", "),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${seoConfig.baseUrl}${article.path}`,
    },
    about: (article.keywords || []).map((name) => ({ "@type": "Thing", name })),
  };

  return (
    <div className="article-page">
      <SEO
        title={article.seoTitle || article.title}
        description={article.seoDescription || article.description}
        path={article.path}
        type="article"
        image={article.image}
        imageAlt={article.imageAlt}
        publishedTime={article.datePublished}
        modifiedTime={article.dateModified || article.datePublished}
        author={publisherName}
        section={article.category}
        tags={article.tags || article.keywords || []}
        schema={[buildBreadcrumbSchema(breadcrumbs), schema, faqSchema].filter(Boolean)}
      />
      <Breadcrumbs items={breadcrumbs} />

      <div className="editorial-story">
        <section className="site-shell editorial-story__shell">
          <article className="min-w-0">
            <div className={article.image ? "editorial-article-lead" : "editorial-article-lead editorial-article-lead--text"}>
              <EditorialArticleHero
                article={article}
                title={article.title}
                subtitle={article.subtitle || article.description}
                category={article.category || "Robotics"}
                topicTag={topicTag}
                author={publisherName}
              />
              <ArticleImage article={article} sizes="(min-width: 1024px) 400px, (min-width: 512px) 480px, calc(100vw - 32px)" />
            </div>
            {article.sponsoredDisclosure && (
              <p className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm leading-7 text-neutral-800" role="note">{article.sponsoredDisclosure}</p>
            )}

            <div className="editorial-story__grid">
              <div className="min-w-0">
                <div className="editorial-story__body">
                  <p className="editorial-story__intro">
                    {article.description}
                  </p>
                  <KeyDetails items={article.keyDetails} />
                  <ArticleAd
                    placement="top"
                    articleSlug={article.slug}
                    className="mt-8"
                  />
                  <XPostEmbed postUrl={article.tweetUrl} sponsored={Boolean(article.sponsoredDisclosure)} />

                  <div className="mt-10 grid gap-10">
                    {(article.sections || []).map((section, index) => (
                      <div key={section.heading}>
                        <section>
                          <h2 className="text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">
                            {section.heading}
                          </h2>
                          <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700 sm:text-lg sm:leading-9">
                            {(section.paragraphs || []).map((paragraph) => (
                              <p key={paragraph}>{paragraph}</p>
                            ))}
                          </div>
                          {(section.subsections || []).map((subsection) => (
                            <div key={subsection.heading} className="mt-7">
                              <h3 className="text-xl font-black tracking-tight text-neutral-950 sm:text-2xl">
                                {subsection.heading}
                              </h3>
                              <div className="mt-3 grid gap-4 text-base leading-8 text-neutral-700 sm:text-lg sm:leading-9">
                                {(subsection.paragraphs || []).map((paragraph) => (
                                  <p key={paragraph}>{paragraph}</p>
                                ))}
                              </div>
                            </div>
                          ))}
                        </section>
                        {index === 1 && <TechCard data={article.techCard} />}
                        {index === 2 && (
                          <ArticleAd
                            index={3}
                            totalSections={article.sections?.length || 5}
                            articleSlug={article.slug}
                            className="mt-8"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <VerificationNotes notes={article.verificationNotes} />
                  <ArticleFaq items={article.faq} />
                  <TopicHubLinks article={article} />
                  <ArticleShareBlock title={article.title} path={article.path} />
                  <ArticleAd
                    placement="bottom"
                    articleSlug={article.slug}
                    className="mt-10"
                  />
                  <Sources sources={article.sourceLinks} sponsored={Boolean(article.sponsoredDisclosure)} />
                  <TechArticleAuthorBox author="@techniahqrobot" />
                  <RelatedLinks links={article.relatedLinks} />
                  <RobbyantAttribution article={article} />
                </div>
              </div>

              <aside className="grid h-fit gap-5 lg:sticky lg:top-24">
                <SidebarAd articleSlug={article.slug} />
                <section className="rounded-[1.6rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
                  <h2 className="text-sm font-black uppercase tracking-[0.18em] text-neutral-500">
                    Latest robotics news
                  </h2>
                  <div className="mt-4 grid gap-3">
                    {relatedInSidebar.map((item) => (
                      <Link
                        key={item.slug}
                        to={item.path}
                        className="rounded-[1.15rem] border border-neutral-200 bg-neutral-50 p-4 text-sm font-bold leading-6 text-neutral-800 transition hover:border-blue-200 hover:text-blue-700"
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </section>
              </aside>
            </div>
          </article>
        </section>
        <FollowTechniaHQ />
      </div>
    </div>
  );
}
